/**
 * AQL APPS - Universal Push Notification System
 * Unified logic for registration and subscription
 */

const SW_PATH = '/sw.js';

// --- Helper Functions ---

import { supabase } from '@/utils/supabase/client';
import { functionsBaseUrl, publicAnonKey } from "/utils/supabase/info";

const VISION_PUSH_BASE = `${functionsBaseUrl}/vision-push`;

interface PushRequestOptions {
  userEmail?: string | null;
  forceResubscribe?: boolean;
}

const enabledStorageKey = (appName: string) => `aql_push_enabled_${appName}`;
const vapidStorageKey = (appName: string) => `aql_push_vapid_${appName}`;

function isProbablyJwt(token?: string | null) {
  return Boolean(token && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token));
}

async function getPushAuthHeaders(authToken?: string | null, includeJson = false) {
  const sessionResult = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
  const bearerToken = sessionResult.data.session?.access_token
    ?? (isProbablyJwt(authToken) ? authToken : publicAnonKey);

  return {
    ...(includeJson ? { 'Content-Type': 'application/json' } : {}),
    apikey: publicAnonKey,
    Authorization: `Bearer ${bearerToken}`,
    ...(authToken ? { 'X-Session-Token': authToken } : {})
  };
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function desktopBridge() {
  return (window as any).aqlDesktop;
}

export function isDesktopNativeNotificationSupported(): boolean {
  return typeof desktopBridge()?.showNotification === 'function';
}

async function showDesktopNotification(title: string, body: string) {
  const notify = desktopBridge()?.showNotification;
  if (typeof notify !== 'function') return false;
  await notify(title, { body });
  return true;
}

// --- Public API for Hooks ---

export function isPushNotificationSupported(): boolean {
  return isDesktopNativeNotificationSupported() || ('serviceWorker' in navigator && 'PushManager' in window);
}

export function getNotificationPermission(): NotificationPermission {
  if (isDesktopNativeNotificationSupported()) return 'granted';
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

export function isNotificationEnabled(appName: string): boolean {
  // Check local storage for persistent preference
  return localStorage.getItem(enabledStorageKey(appName)) === 'true';
}

export async function fetchVapidPublicKey(_apiUrl: string): Promise<string | null> {
  try {
    const response = await fetch(`${VISION_PUSH_BASE}/vapid-public-key`, {
      headers: await getPushAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch VAPID key');
    const { publicKey } = await response.json();
    return publicKey;
  } catch (error) {
    console.error('❌ Error fetching VAPID public key:', error);
    return null;
  }
}

export async function enablePushNotifications(
  appName: string, 
  apiUrl: string, 
  authToken: string,
  vapidPublicKey?: string | null,
  options: PushRequestOptions = {}
): Promise<{ success: boolean; error?: string }> {
  try {
    if (isDesktopNativeNotificationSupported()) {
      localStorage.setItem(enabledStorageKey(appName), 'true');
      await showDesktopNotification('AQL Vision', 'Notificações nativas ativadas neste Mac.');
      return { success: true, error: 'modo desktop nativo' };
    }

    if (!vapidPublicKey) {
      return { success: false, error: 'VAPID public key em falta' };
    }

    // 1. Request Permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Permissão de notificações negada' };
    }

    let registration: ServiceWorkerRegistration;

    // Register Service Worker with fallback
    try {
      const checkRes = await fetch(SW_PATH).catch(() => null);
      const contentType = checkRes?.headers.get('Content-Type') || '';
      
      if (contentType.includes('text/html')) {
        // Try inline fallback directly if static files are not served correctly
        const { registerInlineServiceWorker } = await import('@/app/utils/service-worker-inline');
        registration = await registerInlineServiceWorker();
      } else {
        registration = await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
      }
    } catch (swError) {
      // Fallback to inline
      const { registerInlineServiceWorker } = await import('@/app/utils/service-worker-inline');
      registration = await registerInlineServiceWorker();
    }

    // 3. Wait for readiness
    registration = await navigator.serviceWorker.ready;

    // 4. Subscribe
    const previousVapidKey = localStorage.getItem(vapidStorageKey(appName));
    const existingSubscription = await registration.pushManager.getSubscription();

    if (
      existingSubscription &&
      (options.forceResubscribe || (previousVapidKey && previousVapidKey !== vapidPublicKey))
    ) {
      await existingSubscription.unsubscribe();
    }

    const subscription = await registration.pushManager.getSubscription()
      ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

    // 5. Send to Server
    const response = await fetch(`${VISION_PUSH_BASE}/subscribe`, {
      method: 'POST',
      headers: await getPushAuthHeaders(authToken, true),
      body: JSON.stringify({
        subscription,
        appName,
        userEmail: options.userEmail ?? undefined,
        userAgent: navigator.userAgent,
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: (navigator as any).platform || 'unknown'
        }
      })
    });

    if (!response.ok) {
      throw new Error('Erro ao sincronizar subscrição com o servidor');
    }

    // 6. Save preference
    localStorage.setItem(enabledStorageKey(appName), 'true');
    localStorage.setItem(vapidStorageKey(appName), vapidPublicKey);
    return { success: true };

  } catch (error: any) {
    console.error('❌ Error enabling push:', error);
    return { success: false, error: error.message || 'Erro desconhecido' };
  }
}

export async function disablePushNotifications(
  appName: string,
  apiUrl: string,
  authToken: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (isDesktopNativeNotificationSupported()) {
      localStorage.setItem(enabledStorageKey(appName), 'false');
      return { success: true };
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Notify server
        await fetch(`${VISION_PUSH_BASE}/unsubscribe`, {
          method: 'POST',
          headers: await getPushAuthHeaders(authToken, true),
          body: JSON.stringify({ endpoint: subscription.endpoint })
        }).catch(err => console.warn('Server unsubscribe failed', err));

        await subscription.unsubscribe();
      }
    }

    localStorage.setItem(enabledStorageKey(appName), 'false');
    localStorage.removeItem(vapidStorageKey(appName));
    return { success: true };
  } catch (error: any) {
    console.error('❌ Error disabling push:', error);
    return { success: false, error: error.message || 'Erro desconhecido' };
  }
}

export async function sendTestPushNotification(
  appName: string,
  apiUrl: string,
  authToken: string,
  options: PushRequestOptions = {}
): Promise<boolean> {
  try {
    if (isDesktopNativeNotificationSupported()) {
      return showDesktopNotification('AQL Vision - Teste', 'Notificação nativa recebida neste Mac.');
    }

    const registration = await navigator.serviceWorker.getRegistration().catch(() => null);
    const subscription = await registration?.pushManager.getSubscription().catch(() => null);

    const response = await fetch(`${VISION_PUSH_BASE}/test`, {
      method: 'POST',
      headers: await getPushAuthHeaders(authToken, true),
      body: JSON.stringify({
        appName,
        userEmail: options.userEmail ?? undefined,
        subscription: subscription?.toJSON?.() ?? subscription ?? undefined
      })
    });
    if (!response.ok) return false;
    const payload = await response.json().catch(() => ({}));
    return payload?.ok !== false;
  } catch (error) {
    console.error('❌ Test notification failed:', error);
    return false;
  }
}

export async function sendLocalDemoNotification(appName: string) {
  if (await showDesktopNotification(`AQL ${appName.toUpperCase()}`, 'Teste de notificação nativa/local.')) {
    return;
  }

  if ('serviceWorker' in navigator && Notification.permission === 'granted') {
    const registration = await navigator.serviceWorker.getRegistration().catch(() => null);
    if (registration) {
      await registration.showNotification(`AQL ${appName.toUpperCase()}`, {
        body: 'Teste de notificação local (Modo de Compatibilidade)',
        icon: '/aql-engineering-logo.png',
        badge: '/aql-engineering-logo.png',
        tag: 'aql-local-test',
        data: { url: '/demo' }
      });
      return;
    }
  }

  if (Notification.permission === 'granted') {
    new Notification(`AQL ${appName.toUpperCase()}`, {
      body: 'Teste de notificação local (Modo de Compatibilidade)',
      icon: '/aql-engineering-logo.png'
    });
  }
}
